import React, { useState, useEffect, useRef } from 'react';
import api, { resolveFileUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash, UploadSimple, Image as ImageIcon, ShieldCheck } from '@phosphor-icons/react';

function ImageUploader({ label, kind, currentUrl, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || '');

  useEffect(() => { setPreview(currentUrl || ''); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Format harus PNG, JPG, WEBP, atau GIF'); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Maks 5 MB'); return; }

    setUploading(true);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/uploads/image?kind=${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.url);
      toast.success(`${label} diperbarui`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal upload');
      setPreview(currentUrl || '');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const isCover = kind === 'cover';
  const imgUrl = resolveFileUrl(preview);

  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold block mb-2">{label}</label>
      <div className={`border-2 border-dashed border-foreground bg-muted relative overflow-hidden ${isCover ? 'aspect-[3/1]' : 'w-32 h-32'}`}>
        {imgUrl ? (
          <img src={imgUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImageIcon size={isCover ? 40 : 28} weight="duotone" />
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 border-2 border-foreground bg-primary text-white px-3 py-1.5 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-60"
          data-testid={`upload-${kind}-btn`}
        >
          <UploadSimple size={12} weight="bold" /> {uploading ? 'Upload...' : 'Upload'}
        </button>
        <input type="file" ref={fileRef} onChange={handleFile} accept="image/*" className="hidden" data-testid={`upload-${kind}-file`} />
      </div>
      <p className="text-xs text-muted-foreground mt-1">{isCover ? 'Banner 3:1. ' : ''}PNG/JPG/WEBP, maks 2 MB.</p>
    </div>
  );
}

export default function DashSettings() {
  const { user, setUser, checkAuth } = useAuth();
  const [form, setForm] = useState({ name: '', username: '', bio: '' });
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ 
        name: user.name || '', 
        username: user.username || '', 
        bio: user.bio || '',
        auto_moderation: user.auto_moderation ?? true
      });
      setLinks(user.links || []);
    }
  }, [user]);

  const addLink = () => setLinks([...links, { label: '', url: '' }]);
  const updateLink = (i, key, val) => {
    const c = [...links]; c[i] = { ...c[i], [key]: val }; setLinks(c);
  };
  const removeLink = (i) => setLinks(links.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/profile/me', { ...form, links: links.filter((l) => l.url) });
      setUser({ ...user, ...res.data });
      toast.success('Profil disimpan');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 pt-2 md:pt-0" data-testid="dash-settings">

      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6 space-y-5">
        <div className="grid md:grid-cols-[200px_1fr] gap-5">
          <ImageUploader label="Foto Profil" kind="picture" currentUrl={user?.picture}
            onUploaded={(url) => { setUser({ ...user, picture: url }); checkAuth(); }} />
          <ImageUploader label="Cover Image (Banner)" kind="cover" currentUrl={user?.cover_image}
            onUploaded={(url) => { setUser({ ...user, cover_image: url }); checkAuth(); }} />
        </div>
      </div>

      <form onSubmit={save} className="border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Nama Tampil</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              required data-testid="settings-name-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold block mb-2">Username</label>
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
              className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
              required data-testid="settings-username-input" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold block mb-2">Bio</label>
          <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
            rows={3} maxLength={200}
            className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary resize-none"
            placeholder="Ceritakan tentang dirimu..." data-testid="settings-bio-input" />
        </div>

        <div className="border-2 border-foreground bg-gradient-to-r from-accent/20 to-primary/5 p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 border-2 border-foreground bg-primary text-white flex items-center justify-center shadow-brutal-sm shrink-0">
                <ShieldCheck size={28} weight="bold" />
              </div>
              <div>
                <p className="text-sm font-bold uppercase tracking-tight flex items-center gap-2">
                  AI Protection System <span className="bg-primary text-white text-[9px] px-1.5 py-0.5 rounded-sm font-bold">ANTI-JUDOL</span>
                </p>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-1 max-w-md">
                  Sistem keamanan yang melindungi reputasi Anda dari donasi spam <strong>Judi Online</strong>, kata kasar, dan konten dewasa. Bekerja 24/7 otomatis.
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
              <input type="checkbox" className="sr-only peer" checked={form.auto_moderation} 
                onChange={(e) => setForm({ ...form, auto_moderation: e.target.checked })} />
              <div className="w-11 h-6 bg-muted border-2 border-foreground peer-focus:outline-none peer-checked:bg-primary transition-all after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-2 after:border-foreground after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
            </label>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { emoji: '🛡️', label: 'Anti Judol', desc: '100+ kata kunci gambling' },
              { emoji: '🤬', label: 'Anti Toxic', desc: 'Filter kata kasar ID & EN' },
              { emoji: '🔗', label: 'Anti Link Spam', desc: 'Blokir URL situs judi' },
              { emoji: '📱', label: 'Anti Nomor Spam', desc: 'Sensor nomor HP/WA' },
            ].map((f, i) => (
              <div key={i} className="border-2 border-foreground/20 bg-white/50 p-2.5 text-center">
                <p className="text-lg mb-0.5">{f.emoji}</p>
                <p className="text-[10px] font-bold uppercase">{f.label}</p>
                <p className="text-[9px] text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-widest font-bold block">Link Eksternal</label>
            <button type="button" onClick={addLink}
              className="inline-flex items-center gap-1 border-2 border-foreground bg-accent px-2 py-1 text-xs font-bold uppercase shadow-brutal-sm"
              data-testid="add-link-btn">
              <Plus size={12} weight="bold" /> Tambah
            </button>
          </div>
          <div className="space-y-2">
            {links.map((l, i) => (
              <div key={i} className="flex gap-2" data-testid={`link-row-${i}`}>
                <input value={l.label} onChange={(e) => updateLink(i, 'label', e.target.value)}
                  placeholder="Instagram" className="w-32 border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm focus:outline-none focus:border-primary" />
                <input value={l.url} onChange={(e) => updateLink(i, 'url', e.target.value)}
                  placeholder="https://instagram.com/..." className="flex-1 border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm focus:outline-none focus:border-primary" />
                <button type="button" onClick={() => removeLink(i)} className="w-9 h-9 border-2 border-foreground bg-destructive text-white flex items-center justify-center shadow-brutal-sm" data-testid={`link-remove-${i}`}>
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={saving}
          className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-5 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60"
          data-testid="settings-save-btn">
          {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
      </form>
    </div>
  );
}
